import { CID } from 'multiformats/cid';

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata } from '@/types/poc/helia';
import { setUploadProgress } from '@/store/slices/poc/heliaSlice';
import { sendFileAnnouncement } from '@/store/slices/poc/messagingSlice';
import { verifyDagIntegrity } from '@/utils/fileOperations';

interface RetryFileAnnouncementParams {
  fileName: string;
  cid: string;
  sixId: string;
  metadata: FileMetadata;
  retryCount: number;
}

const RETRY_PROGRESS = 80;

/**
 * Retry a file announcement after checking that the CID's data is still
 * readable from the local blockstore. If the DAG is corrupt (e.g. the
 * `yield* is not iterable` error), the retry is aborted with a clear
 * error message instead of re-announcing a broken CID.
 */
export const retryFileAnnouncement = createAppAsyncThunk(
  'helia/retryFileAnnouncement',
  async(
    { fileName, cid, sixId, metadata, retryCount }: RetryFileAnnouncementParams,
    { dispatch, extra: { pocServices } }
  ) => {
    const heliaNode = pocServices.p2pStackManagerService.getHeliaNode(sixId);

    if (heliaNode === null) {
      dispatch(setUploadProgress({
        fileName,
        stage: 'error',
        progress: 0,
        error: 'P2P node not connected. Please refresh and try again.',
        retryCount,
      }));
      return;
    }

    // Check if the CID data can be read back from the local blockstore
    // Note: metadata.size is the original (pre-encryption) file size, not the
    // encrypted size stored under this CID, so we skip the size check.
    const parsedCid = CID.parse(cid);
    const verification = await verifyDagIntegrity(heliaNode, parsedCid);

    if (!verification.valid) {
      dispatch(setUploadProgress({
        fileName,
        stage: 'error',
        progress: 0,
        error: 'File data is corrupt and cannot be retried. Please re-upload the file.',
        retryCount,
      }));
      return;
    }

    // CID is valid — proceed with re-announcement
    dispatch(setUploadProgress({
      fileName,
      stage: 'announcing',
      progress: RETRY_PROGRESS,
      retryCount,
    }));

    void dispatch(sendFileAnnouncement({
      sixId,
      cid,
      metadata,
      attemptCount: 1,
    }));
  }
);
